import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Link as MuiLink,
  Skeleton,
  IconButton,
  InputAdornment,
  Chip,
  Grid2,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import Header from '../Header';
import Footer from '../Footer';
import { API_URL } from '../Config';
import config from "../common/Config";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../utils/companySettingsCache";
import { encryptPayload, normalizeEncryptedApiResponse } from "../../utils/customerCrypto";
import { Iconify } from "src/components/iconify";
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';


const PickingList = () => {
  const location = useLocation();
  const inTabs = location.pathname === '/picking/picking_list';
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [newSales, setNewSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isSingleOrderPicking, setIsSingleOrderPicking] = useState(false);
  const accessToken = localStorage.getItem('accessToken');

  const columns = useMemo(() => {
    const list = [
      { id: "picklist_number", label: "Picklist No", width: 120 },
    ];
    if (isSingleOrderPicking) {
      list.push({ id: "customer", label: "Customer Name", width: 200 });
    } else {
      list.push({ id: "total_sales_count", label: "Invoices", width: 120 });
    }
    list.push(
      { id: "total", label: "Total", width: 100 },
      { id: "route_name", label: "Route Name", width: 120 },
      { id: "name", label: "Picker", width: 200 },
      { id: "print_on", label: "Picklist Date", width: 150 },
      { id: "picking_status", label: "Picking Status", width: 150 },
      { id: "action", label: "Action", width: 100 },
    );
    return list;
  }, [isSingleOrderPicking]);

  const fetchData = async () => {
    const accessToken = localStorage.getItem('accessToken');
    const token = localStorage.getItem("token");
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    setLoading(true);
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      const headers = { authorization: `Bearer ${token}` };
      let body;
      if (useEncrypt) {
        const plain = {
          searchByFromdate: fromDate,
          searchByTodate: toDate,
          draw: "1",
          start: "0",
          length: "1000",
          role: selectedRole,
        };
        const ep = encryptPayload(plain);
        if (!ep) {
          setNewSales([]);
          return;
        }
        const fd = new FormData();
        fd.append("encrypted_payload", ep);
        body = fd;
      } else {
        const formData = new FormData();
        formData.append('searchByFromdate', fromDate);
        formData.append('searchByTodate', toDate);
        formData.append('draw', 1);
        formData.append('start', 0);
        formData.append('length', 1000);
        formData.append('role', selectedRole);
        body = formData;
      }

      const response = await fetch(`${API_URL}/picking/list`, {
        method: 'POST',
        headers,
        body,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch new sales data');
      }

      const json = await response.json();
      const data = normalizeEncryptedApiResponse(json, useEncrypt) ?? json;
      setNewSales(Array.isArray(data.data) ? data.data : []);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const deletePicking = async (picklistNumber) => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
    const token = localStorage.getItem("token");

    // Create FormData object
    const formData = new FormData();
    formData.append('picklist_number', picklistNumber);

    try {
      const response = await fetch(`${API_URL}/remove_picklist`, {
        method: 'POST',
        headers: {
          'authorization': `Bearer ${token}`,
          // Note: FormData automatically sets Content-Type to multipart/form-data
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to delete picking item');
      }

      await response.json();
      fetchData();
      toast.success('Your picking item has been deleted.');
    } catch (error) {
      console.error('Error deleting picking item:', error);
      toast.error('There was an error deleting the item.');
    }
  };


  const confirmDelete = async (picklistNumber) => {
    if (window.confirm('Are you sure? You won\'t be able to revert this!')) {
      deletePicking(picklistNumber);
    }
  };

  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleEdit = () => {
    if (selectedRow) {
      window.location.href = `edit_picking/${selectedRow.picklist_number}`;
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (selectedRow) {
      confirmDelete(selectedRow.picklist_number);
    }
    handleMenuClose();
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      const settings = await getCompanySettings();
      if (!mounted) return;
      setIsSingleOrderPicking(settings?.isSingleOrderPicking === "Y");
    })();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [fromDate, toDate, selectedRole]);

  // Filter orders based on search term
  const filteredSales = useMemo(() => {
    if (!searchTerm.trim()) {
      return newSales;
    }
    const searchLower = searchTerm.toLowerCase().trim();
    return newSales.filter((item) => {
      return (
        (item.picklist_number && String(item.picklist_number).toLowerCase().includes(searchLower)) ||
        (item.customer && String(item.customer).toLowerCase().includes(searchLower)) ||
        (item.route_name && item.route_name.toLowerCase().includes(searchLower)) ||
        (item.name && item.name.toLowerCase().includes(searchLower)) ||
        (item.picking_status && item.picking_status.toLowerCase().includes(searchLower)) ||
        (item.print_on && item.print_on.toLowerCase().includes(searchLower))
      );
    });
  }, [newSales, searchTerm]);

  // Reset page when search term changes
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get paginated data
  const paginatedSales = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredSales.slice(startIndex, endIndex);
  }, [filteredSales, page, rowsPerPage]);

  const renderCell = (columnId, row) => {
    if (columnId === "picklist_number") {
      const url = (isSingleOrderPicking)
        ? `${config.BASE_URL}/sales/single_view_picking_list/${row.picklist_number}`
        : `${config.BASE_URL}/sales/view_picking_list/${row.picklist_number}`;
      return (
        <MuiLink
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: "inherit",
            textDecoration: "underline !important",
            textDecorationColor: "currentColor",
            textUnderlineOffset: "2px",
            "&:hover": {
              textDecoration: "underline !important",
              textDecorationColor: "currentColor"
            }
          }}
        >
          {row.picklist_number}
        </MuiLink>
      );
    }
    if (columnId === "total") {
      const products = row.total_product ?? 0;
      const qty = row.total_qty ?? 0;
      return `${products}/${qty}`;
    }
    if (columnId === "picking_status") {
      const color = row.picking_status === "Completed" ? "success" : row.picking_status === "Pending" ? "warning" : "default";
      return (
        <Chip
          label={row.picking_status}
          color={color}
          variant="soft"
          size="small"
          sx={{ fontWeight: 500, fontSize: "0.7rem", px: 1 }}
        />
      );
    }
    if (columnId === "action") {
      if (row.is_lock == 0) {
        return (
          <Box sx={{ display: "flex", justifyContent: "center", position: "relative" }}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleMenuOpen(e, row);
              }}
              sx={{ color: "#637381" }}
            >
              <Iconify icon="eva:more-vertical-fill" width={20} height={20} />
            </IconButton>
          </Box>
        );
      }
      return <Typography sx={{ color: "#637381", fontSize: "0.75rem" }}>-</Typography>;
    }
    return row[columnId] ?? "";
  };

  return (
    <>
      {!inTabs && <Header />}
      <Box sx={{ width: "100%", px: inTabs ? 0 : { xs: 2, md: 3 }, py: inTabs ? 0 : 2, backgroundColor: inTabs ? "transparent" : "#f5f7fa" }}>
        <Paper
        >
          {/* Filter Section */}
          <Box sx={{ p:1}}>
            <Grid2 container spacing={2} alignItems="center">
              <Grid2 size={{ xs: 12, md: 8 }}>
                <Typography className="page-title">
                  Picking List
                </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 4 }}>
                <TextField
                  className="input-border"
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  placeholder="Search picking list..."
           
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <Iconify icon="eva:search-outline"/>
                        </InputAdornment>
                      )
                    }
                  }}
              
                />
              </Grid2>
            </Grid2>
          </Box>

          {/* Table Section */}
          <TableContainer sx={{ width: "100%", overflowX: "auto", position: "relative" }}>
            <Table
              size="medium"
              className="table-cell-border"
              sx={{
                tableLayout: "fixed",
              }}
            >
              <colgroup>
                {columns.map((column) => (
                  <col key={column.id} style={{ width: column.width ? `${column.width}px` : "auto" }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow className="table-cell-header">
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.id === "action" ? "center" : column.numeric ? "right" : "left"}
                      sx={{
                   
                        width: column.width ? `${column.width}px` : "auto"
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: 10 }).map((_, index) => (
                    <TableRow key={`skeleton-${index}`}>
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.id === "action" ? "center" : column.numeric ? "right" : "left"}
                          sx={{ py: 1, fontSize: "0.75rem", width: column.width ? `${column.width}px` : "auto" }}
                        >
                          <Skeleton variant="text" width="60%" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : paginatedSales.length > 0 ? (
                  paginatedSales.map((row) => (
                    <TableRow
                      key={row.picklist_number}
                      hover
                      className="table-cell-style"
                    >
                      {columns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.id === "action" ? "center" : column.numeric ? "right" : "left"}
                          sx={{
                          
                            width: column.width ? `${column.width}px` : "auto",
                            ...(column.numeric && { fontWeight: 500 })
                          }}
                        >
                          {renderCell(column.id, row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns.length} sx={{ py: 8 }}>
                      <Typography align="center" sx={{ color: "#637381", fontSize: "0.75rem" }}>
                        No picking list found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Section */}
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <TablePagination
              component="div"
              count={filteredSales.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
              className="pagination-styles"
            />
          </Box>
        </Paper>
      </Box>
      {!inTabs && <Footer />}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 160,
              boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
              borderRadius: 1,
              maxHeight: 'calc(100vh - 100px)',
            },
          },
        }}
        MenuListProps={{
          sx: {
            py: 0.5,
          },
        }}
        disableAutoFocusItem
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <Iconify icon="eva:edit-fill" width={20} height={20} />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <Iconify icon="eva:trash-2-fill" width={20} height={20} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default PickingList;
